import { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { Link } from 'react-router-dom';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { motion, AnimatePresence } from 'motion/react';
import { darkenHex, accentRgba } from './utils.js';
import { JOB_TABS } from './constants.js';
import JobCard from './JobCard.jsx';

const PAGE_SIZE = 4;

function RecommendedJobs({
	activeTab,
	onTabChange,
	jobs,
	jobCounts,
	loading,
	appliedJobIds,
	accentColor,
	onInterested,
}) {
	const [pageByTab, setPageByTab] = useState({ nearby: 0, department: 0, company: 0 });

	useEffect(() => {
		setPageByTab((prev) => ({ ...prev, [activeTab]: 0 }));
	}, [jobs.length]);

	const page = pageByTab[activeTab] || 0;
	const totalPages = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));
	const pageJobs = jobs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
	const canPrev = page > 0;
	const canNext = page < totalPages - 1;

	const setPage = (p) => {
		setPageByTab((prev) => ({ ...prev, [activeTab]: p }));
	};

	return (
		<section
			className="rounded-2xl bg-white"
			style={{
				border: '1px solid #f1f5f9',
				padding: 20,
				boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
			}}
		>
			<div
				className="flex flex-wrap items-center justify-between gap-3"
				style={{ marginBottom: 16 }}
			>
				<h2
					style={{
						fontSize: 16,
						fontWeight: 600,
						color: '#1e293b',
						margin: 0,
					}}
				>
					Recommended Jobs
				</h2>
				<Link
					to="/browsejobs"
					style={{
						color: accentColor,
						fontSize: 12,
						fontWeight: 600,
						textDecoration: 'none',
						transition: 'color 0.2s',
					}}
					onMouseOver={(e) => {
						e.currentTarget.style.color = darkenHex(accentColor);
					}}
					onMouseOut={(e) => {
						e.currentTarget.style.color = accentColor;
					}}
				>
					View More &rarr;
				</Link>
			</div>

			<div
				className="flex items-center"
				style={{
					marginBottom: 18,
					borderBottom: '1px solid #f1f5f9',
				}}
			>
				<div className="flex flex-wrap gap-2" style={{ flex: 1 }}>
					{JOB_TABS.map((tab) => {
						const isActive = tab.key === activeTab;
						return (
							<button
								key={tab.key}
								type="button"
								onClick={() => onTabChange(tab.key)}
								style={{
									padding: '8px 14px',
									background: isActive
										? accentRgba(accentColor, 0.1)
										: 'transparent',
									color: isActive ? accentColor : '#475569',
									border: 'none',
									borderRadius: '8px 8px 0 0',
									fontWeight: 600,
									fontSize: 13,
									cursor: 'pointer',
									borderBottom: `2px solid ${isActive ? accentColor : 'transparent'}`,
									marginBottom: -1,
									transition: 'color 0.2s, background 0.2s',
								}}
							>
								{tab.label} ({jobCounts?.[tab.key]?.length || 0})
							</button>
						);
					})}
				</div>

				{jobs.length > PAGE_SIZE && (
					<div
						className="flex items-center gap-1"
						style={{ flexShrink: 0, marginBottom: -1 }}
					>
						<button
							type="button"
							disabled={!canPrev}
							onClick={() => setPage(page - 1)}
							style={{
								width: 28,
								height: 28,
								borderRadius: 8,
								border: 'none',
								background: canPrev ? accentRgba(accentColor, 0.1) : '#f8fafc',
								color: canPrev ? accentColor : '#cbd5e1',
								cursor: canPrev ? 'pointer' : 'default',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 0,
								transition: 'all 0.2s',
							}}
						>
							<LuChevronLeft size={16} />
						</button>
						<button
							type="button"
							disabled={!canNext}
							onClick={() => setPage(page + 1)}
							style={{
								width: 28,
								height: 28,
								borderRadius: 8,
								border: 'none',
								background: canNext ? accentRgba(accentColor, 0.1) : '#f8fafc',
								color: canNext ? accentColor : '#cbd5e1',
								cursor: canNext ? 'pointer' : 'default',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 0,
								transition: 'all 0.2s',
							}}
						>
							<LuChevronRight size={16} />
						</button>
					</div>
				)}
			</div>

			<AnimatePresence mode="wait">
				<motion.div
					key={`${activeTab}-${page}`}
					initial={{ opacity: 0, y: 6 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -6 }}
					transition={{ duration: 0.2 }}
				>
					{loading ? (
						<div
							className="flex items-center justify-center"
							style={{ padding: '32px 0' }}
						>
							<Spin size="small" />
						</div>
					) : pageJobs.length === 0 ? (
						<p
							style={{
								fontSize: 13,
								color: '#475569',
								textAlign: 'center',
								padding: '32px 0',
								margin: 0,
							}}
						>
							No jobs to show
						</p>
					) : (
						<div
							className="grid gap-4"
							style={{
								gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
							}}
						>
							{pageJobs.map((match) => (
								<JobCard
									key={match.id}
									match={match}
									applied={appliedJobIds.has(match.job?.id)}
									accentColor={accentColor}
									onInterested={onInterested}
								/>
							))}
						</div>
					)}
				</motion.div>
			</AnimatePresence>
		</section>
	);
}

export default RecommendedJobs;
